import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/components/feature/Toast";
import EmptyState from "@/pages/admin/dashboard/components/EmptyState";
import ModerationTab from "@/pages/admin/dashboard/components/ModerationTab";
import ApplicationCard from "./components/ApplicationCard";
import ReasonDialog from "./components/ReasonDialog";
import {
  fetchReviewApplications,
  approveSeller,
  rejectSeller,
  suspendSeller,
  type ReviewApplication,
} from "@/lib/seller-review";
import type {
  AdminProduct,
  AdminProfile,
} from "@/pages/admin/dashboard/types";

type TabKey =
  | "publicaciones"
  | "pending"
  | "approved"
  | "rejected"
  | "suspended";

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: "publicaciones", label: "Publicaciones", icon: "ri-file-list-3-line" },
  { key: "pending", label: "Pendientes", icon: "ri-time-line" },
  { key: "approved", label: "Aprobados", icon: "ri-checkbox-circle-line" },
  { key: "rejected", label: "Rechazados", icon: "ri-close-circle-line" },
  { key: "suspended", label: "Suspendidos", icon: "ri-pause-circle-line" },
];

function matchesTab(app: ReviewApplication, tab: TabKey): boolean {
  const s = app.application.status;
  if (tab === "pending") return s === "submitted" || s === "under_review";
  return s === tab;
}

export default function SellerApprovalPage() {
  const { profile } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [applications, setApplications] = useState<ReviewApplication[]>([]);
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [profiles, setProfiles] = useState<AdminProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("publicaciones");
  const [dialog, setDialog] = useState<{
    type: "reject" | "suspend";
    userId: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [apps, prodRes, profRes] = await Promise.all([
        fetchReviewApplications(),
        supabase
          .from("products")
          .select("*")
          .order("created_at", { ascending: false }),
        supabase
          .from("profiles")
          .select("*")
          .order("created_at", { ascending: false }),
      ]);
      if (prodRes.error) throw prodRes.error;
      if (profRes.error) throw profRes.error;
      setApplications(apps.filter((a) => a.application.status !== "draft"));
      setProducts((prodRes.data ?? []) as AdminProduct[]);
      setProfiles((profRes.data ?? []) as AdminProfile[]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "No se pudieron cargar las solicitudes."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const pendingProducts = useMemo(
    () => products.filter((p) => p.status === "pending_moderation"),
    [products]
  );

  const filtered = useMemo(
    () => applications.filter((a) => matchesTab(a, activeTab)),
    [applications, activeTab]
  );

  const counts = useMemo(() => {
    const c: Record<TabKey, number> = {
      publicaciones: pendingProducts.length,
      pending: 0,
      approved: 0,
      rejected: 0,
      suspended: 0,
    };
    for (const a of applications) {
      if (matchesTab(a, "pending")) c.pending++;
      else if (matchesTab(a, "approved")) c.approved++;
      else if (matchesTab(a, "rejected")) c.rejected++;
      else if (matchesTab(a, "suspended")) c.suspended++;
    }
    return c;
  }, [applications, pendingProducts]);

  async function handleApprove(userId: string) {
    setBusy(true);
    try {
      await approveSeller(userId);
      showToast(t("notify.admin.approved"), "success");
      await fetchAll();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "No se pudo aprobar.",
        "error"
      );
    } finally {
      setBusy(false);
    }
  }

  function openReject(userId: string) {
    setDialog({ type: "reject", userId });
  }

  function openSuspend(userId: string) {
    setDialog({ type: "suspend", userId });
  }

  async function confirmDialog(reason: string) {
    if (!dialog) return;
    setBusy(true);
    try {
      if (dialog.type === "reject") {
        await rejectSeller(dialog.userId, reason);
        showToast(t("notify.admin.rejected"), "success");
      } else {
        await suspendSeller(dialog.userId, reason);
        showToast(t("notify.admin.suspended"), "success");
      }
      setDialog(null);
      await fetchAll();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "No se pudo completar la acción.",
        "error"
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-background-50">
      <main className="mx-auto px-4 py-8 md:px-6 lg:max-w-7xl">
        {/* Encabezado */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
              <i className="ri-store-2-line text-xl text-primary-500"></i>
            </span>
            <div>
              <h1 className="font-heading text-xl font-bold text-foreground-950">
                Aprobación de vendedores
              </h1>
              <p className="text-sm text-foreground-600">
                Gestioná solicitudes de vendedores y publicaciones en revisión.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={fetchAll}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap self-start rounded-md border border-background-200 px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 sm:ml-auto sm:self-auto"
          >
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
            Actualizar
          </button>
        </div>

        {/* Pestañas */}
        <div className="inline-flex flex-wrap items-center gap-1 rounded-full border border-background-200/70 bg-background-100 p-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                activeTab === tab.key
                  ? "bg-primary-500 text-white"
                  : "text-foreground-500 hover:text-foreground-900"
              }`}
            >
              <i className={`${tab.icon} text-base`}></i>
              {tab.label}
              <span
                className={`rounded-full px-1.5 py-0.5 text-xs font-semibold ${
                  activeTab === tab.key
                    ? "bg-white/20 text-white"
                    : "bg-background-200 text-foreground-600"
                }`}
              >
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Contenido */}
        <div className="mt-4 rounded-lg border border-background-200/70 bg-background-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-20 text-center">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
              <p className="text-sm text-foreground-500">Cargando solicitudes…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100">
                <i className="ri-error-warning-line text-2xl text-accent-700"></i>
              </span>
              <p className="mt-4 text-sm font-semibold text-foreground-900">
                Ocurrió un error
              </p>
              <p className="mt-1 max-w-sm text-sm text-foreground-500">{error}</p>
              <button
                type="button"
                onClick={fetchAll}
                className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Reintentar
              </button>
            </div>
          ) : activeTab === "publicaciones" ? (
            <ModerationTab
              products={pendingProducts}
              profiles={profiles}
              onRefresh={fetchAll}
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="ri-inbox-line"
              title="Sin solicitudes"
              description={`No hay vendedores ${
                activeTab === "pending"
                  ? "pendientes de revisión"
                  : activeTab === "approved"
                    ? "aprobados"
                    : activeTab === "rejected"
                      ? "rechazados"
                      : "suspendidos"
              }.`}
            />
          ) : (
            filtered.map((item) => (
              <ApplicationCard
                key={item.application.id}
                item={item}
                onApprove={handleApprove}
                onReject={openReject}
                onSuspend={openSuspend}
              />
            ))
          )}
        </div>
      </main>

      {/* Diálogo de motivo */}
      {dialog && (
        <ReasonDialog
          title={
            dialog.type === "reject"
              ? "Rechazar solicitud"
              : "Suspender vendedor"
          }
          confirmLabel={dialog.type === "reject" ? "Rechazar" : "Suspender"}
          placeholder="Escribí el motivo…"
          busy={busy}
          onConfirm={confirmDialog}
          onCancel={() => setDialog(null)}
        />
      )}
    </div>
  );
}